import { useState, useEffect, useRef } from 'react';
import { truncateText } from '@/utils/truncateText';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Message from '@/components/Message';
import type {
  AgentApiProvidedJobPostsRecord,
  AgentApiSharedJobPostsItem,
} from '@/types/serializers';
import useBulkSuggestJobPosts from '../../provided_job_posts/api/useBulkSuggestJobPosts';
import useBulkSuggestSharedJobPostCandidates from '../../shared_job_posts/api/useBulkSuggestSharedJobPostCandidates';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import CandidatesJobPostEndpointsNewModalAddContents from './CandidatesJobPostEndpointsNewModalAddContents';

type Props = {
  candidateIds: number[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

/** 自社求人とシェア求人を同じカードで並べるための正規化形 */
type ConfirmJobPost = {
  key: string;
  title: string;
  companyName: string | null;
  jobPositionName: string | null;
  salaryInfo: string | null;
  workingLocation: string | null;
};

const toConfirmJobPost = (jobPost: AgentApiProvidedJobPostsRecord): ConfirmJobPost => ({
  key: `provided-${jobPost.id}`,
  title: jobPost.title,
  companyName: jobPost.company?.name ?? null,
  jobPositionName: jobPost.job_position_name,
  salaryInfo: jobPost.salary_info,
  workingLocation: jobPost.working_location,
});

const toConfirmSharedJobPost = (sharedJobPost: AgentApiSharedJobPostsItem): ConfirmJobPost => ({
  key: `shared-${sharedJobPost.id}`,
  title: sharedJobPost.title,
  companyName: sharedJobPost.company_name,
  jobPositionName: sharedJobPost.job_position_name,
  salaryInfo: sharedJobPost.salary_info,
  workingLocation: sharedJobPost.working_location_label,
});

function ContentsCard({ jobPost }: { jobPost: ConfirmJobPost }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const titleRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        const isTruncated = titleRef.current.scrollHeight > titleRef.current.clientHeight;
        setIsTitleTruncated(isTruncated);
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    if (titleRef.current) {
      resizeObserver.observe(titleRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [jobPost.title]);

  return (
    <div className="flex flex-col gap-sm rounded p-lg shadow-[0px_0px_2px_0px_rgba(0,_0,_0,_0.20)]">
      <div className="relative">
        <p
          ref={titleRef}
          className="line-clamp-1 text-lg font-bold"
          onMouseEnter={() => isTitleTruncated && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {jobPost.title}
        </p>
        {showTooltip && isTitleTruncated && (
          <div className="absolute top-full left-1/2 z-10 mt-xs max-w-[540px] -translate-x-1/2 rounded-xs bg-black p-xs shadow-lg">
            <div
              className="absolute top-[-6px] left-1/2 h-2 w-3 -translate-x-1/2 bg-black"
              style={{ clipPath: 'polygon(45.45% 0%, 0% 100%, 100% 100%)' }}
            ></div>
            <p className="text-xs leading-[170%] break-words text-white">{jobPost.title}</p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-md">
        <div className="flex gap-md">
          {jobPost.companyName && (
            <div className="flex gap-2 border-r border-solid border-border pr-md">
              <p className="text-xs font-bold">企業名</p>
              <p className="text-xs">{jobPost.companyName}</p>
            </div>
          )}
          {jobPost.jobPositionName && (
            <div className="flex gap-2 border-r border-solid border-border pr-md">
              <p className="text-xs font-bold">ポジション</p>
              <p className="text-xs" title={jobPost.jobPositionName}>
                {truncateText(jobPost.jobPositionName)}
              </p>
            </div>
          )}
          {jobPost.salaryInfo && (
            <div className="flex gap-2">
              <p className="text-xs font-bold">年収例</p>
              <p className="text-xs">{jobPost.salaryInfo}</p>
            </div>
          )}
        </div>
        {jobPost.workingLocation && (
          <div className="flex gap-2">
            <p className="text-xs font-bold">勤務地</p>
            <p className="text-xs">{jobPost.workingLocation}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BulkSuggestJobPostsModal({
  candidateIds,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const [step, setStep] = useState<'add' | 'confirm'>('add');
  const [selectedJobPosts, setSelectedJobPosts] = useState<AgentApiProvidedJobPostsRecord[]>([]);
  const [selectedSharedJobPosts, setSelectedSharedJobPosts] = useState<
    AgentApiSharedJobPostsItem[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: bulkSuggestJobPosts } = useBulkSuggestJobPosts();
  const { mutateAsync: bulkSuggestSharedJobPosts } = useBulkSuggestSharedJobPostCandidates();
  const confirmJobPosts = [
    ...selectedJobPosts.map(toConfirmJobPost),
    ...selectedSharedJobPosts.map(toConfirmSharedJobPost),
  ];

  const handleClose = () => {
    setStep('add');
    setSelectedJobPosts([]);
    setSelectedSharedJobPosts([]);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (confirmJobPosts.length === 0) return;

    setIsSubmitting(true);
    try {
      if (selectedJobPosts.length > 0) {
        await bulkSuggestJobPosts({
          jobPostIds: selectedJobPosts.map((jobPost) => jobPost.id),
          candidateProfileIds: candidateIds,
        });
      }
      // シェア求人は自社求人へ複製したうえで提案するため専用APIを通す (Issue #3426)
      if (selectedSharedJobPosts.length > 0) {
        await bulkSuggestSharedJobPosts({
          sharedJobPostIds: selectedSharedJobPosts.map((sharedJobPost) => sharedJobPost.id),
          candidateProfileIds: candidateIds,
        });
      }
      showSuccessToast('求人を提案しました');
      setStep('add');
      setSelectedJobPosts([]);
      setSelectedSharedJobPosts([]);
      onSuccess();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '求人の提案に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-max p-0">
        {step === 'add' && (
          <CandidatesJobPostEndpointsNewModalAddContents
            setStep={setStep}
            close={handleClose}
            selectedJobPosts={selectedJobPosts}
            setSelectedJobPosts={setSelectedJobPosts}
            selectedSharedJobPosts={selectedSharedJobPosts}
            setSelectedSharedJobPosts={setSelectedSharedJobPosts}
          />
        )}
        {step === 'confirm' && (
          <div className="w-[1100px] rounded bg-white">
            <div className="flex h-[56px] items-center border-b border-solid border-border px-lg">
              <p className="text-base font-bold">求人の提案確認</p>
            </div>
            <div className="hidden-scrollbar max-h-[590px] overflow-y-auto p-lg">
              {selectedSharedJobPosts.length > 0 && (
                <Message color="caution" className="mb-md">
                  <p className="text-sm">シェア求人を提案すると自社求人へ複製されます。</p>
                </Message>
              )}
              <p>
                {candidateIds.length}件の求職者に以下の{confirmJobPosts.length}
                件の求人を提案します
              </p>
              <div className="mt-md flex flex-col gap-md">
                {confirmJobPosts.map((jobPost) => (
                  <ContentsCard key={jobPost.key} jobPost={jobPost} />
                ))}
              </div>
            </div>
            <div className="flex h-[74px] items-center justify-end gap-md border-t border-solid border-border px-lg">
              <Button color="secondary" onClick={() => setStep('add')}>
                戻る
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || confirmJobPosts.length === 0}
              >
                {isSubmitting ? '送信中...' : '提案する'}
              </Button>
            </div>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
